import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardBody, CardHeader, CardTitle } from '../../ui/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { loadAllGuardsForExport, GuardData } from '../../../pages/manage_guard/service/guardService';
import { FiShield, FiUserCheck, FiUser, FiMapPin, FiTrendingUp } from 'react-icons/fi';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]} !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
    border-color: ${theme.colors.primary[200]};
  }
`;

const MetricIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.xl};
  background: linear-gradient(135deg, ${({ color }) => color}20, ${({ color }) => color}10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
  transition: transform 0.3s ease;

  ${MetricCard}:hover & {
    transform: scale(1.1);
  }

  svg {
    color: ${({ color }) => color};
    font-size: 28px;
  }
`;

const MetricValue = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[2]};
  line-height: 1;
  letter-spacing: -0.03em;
`;

const MetricLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: 1.4;
`;

interface Metric {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid ${theme.colors.light.border.light};
  margin-bottom: ${theme.spacing[6]};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  padding: ${theme.spacing[2]};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const COLORS = [
  theme.colors.primary[500],
  theme.colors.primary[600],
  theme.colors.primary[400],
  theme.colors.primary[700],
  theme.colors.primary[300],
];

const GuardsAnalytics: React.FC = () => {
  const { companyData, user } = useAuth();
  const [guards, setGuards] = useState<GuardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusChartData, setStatusChartData] = useState<Array<{ name: string; value: number }>>([]);
  const [deploymentChartData, setDeploymentChartData] = useState<Array<{ name: string; value: number }>>([]);

  const companyId = companyData?.id || user?.uid || '';

  useEffect(() => {
    const fetchGuardsData = async () => {
      try {
        const guardsData = await loadAllGuardsForExport(companyId);
        setGuards(guardsData);
        
        // Prepare status chart data
        const activeCount = guardsData.filter((g) => g.isActive !== false).length;
        const inactiveCount = guardsData.filter((g) => g.isActive === false).length;
        setStatusChartData([
          { name: 'Active', value: activeCount },
          { name: 'Inactive', value: inactiveCount },
        ]);
        
        // Prepare deployment chart data
        const deployedCount = guardsData.filter((g) => g.currentDeployment).length;
        const undeployedCount = guardsData.length - deployedCount;
        setDeploymentChartData([
          { name: 'Deployed', value: deployedCount },
          { name: 'Undeployed', value: undeployedCount },
        ]);
      } catch (error) {
        console.error('Error fetching guards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGuardsData();
  }, [companyId]);

  // Calculate metrics
  const totalGuards = guards.length;
  const activeGuards = guards.filter((g) => g.isActive !== false).length;
  const inactiveGuards = guards.filter((g) => g.isActive === false).length;
  const deployedGuards = guards.filter((g) => g.currentDeployment).length;
  const guardsWithoutSalary = guards.filter((g) => !g.salary || g.salary === '' || g.salary === 0).length;
  
  // Calculate total salary
  const totalSalary = guards.reduce((sum, g) => {
    if (g.salary) {
      const salaryValue = typeof g.salary === 'number' ? g.salary : parseFloat(String(g.salary).replace(/[^0-9.]/g, '')) || 0;
      return sum + salaryValue;
    }
    return sum;
  }, 0);

  const metrics: Metric[] = [
    {
      icon: <FiShield />,
      label: 'Total Guards',
      value: loading ? '...' : totalGuards.toString(),
      color: theme.colors.primary[500],
    },
    {
      icon: <FiUserCheck />,
      label: 'Active Guards',
      value: loading ? '...' : activeGuards.toString(),
      color: theme.colors.accent.success,
    },
    {
      icon: <FiUser />,
      label: 'Inactive Guards',
      value: loading ? '...' : inactiveGuards.toString(),
      color: theme.colors.accent.danger,
    },
    {
      icon: <FiMapPin />,
      label: 'Deployed Guards',
      value: loading ? '...' : deployedGuards.toString(),
      color: theme.colors.primary[600],
    },
    {
      icon: <FiTrendingUp />,
      label: 'Total Salary',
      value: loading ? '...' : (totalSalary > 0 ? `Rs. ${totalSalary.toLocaleString()}` : '—'),
      color: theme.colors.accent.info,
    },
    {
      icon: <FiShield />,
      label: 'Without Salary',
      value: loading ? '...' : guardsWithoutSalary.toString(),
      color: theme.colors.accent.warning,
    },
  ];

  return (
    <div>
      <MetricsGrid>
        {metrics.map((metric, index) => (
          <MetricCard key={index}>
            <MetricIcon color={metric.color}>{metric.icon}</MetricIcon>
            <MetricValue>{metric.value}</MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </MetricsGrid>
      
      <ChartsGrid>
        <ChartCard>
          <CardHeader>
            <CardTitle>Guard Status Distribution</CardTitle>
          </CardHeader>
          <CardBody>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardBody>
        </ChartCard>

        <ChartCard>
          <CardHeader>
            <CardTitle>Guard Deployment Status</CardTitle>
          </CardHeader>
          <CardBody>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deploymentChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                  <XAxis
                    dataKey="name"
                    stroke={theme.colors.light.text.secondary}
                    fontSize={12}
                    tick={{ fill: theme.colors.light.text.secondary }}
                  />
                  <YAxis
                    stroke={theme.colors.light.text.secondary}
                    tick={{ fill: theme.colors.light.text.secondary }}
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.colors.white,
                      border: `1px solid ${theme.colors.light.border.medium}`,
                      borderRadius: theme.borderRadius.md,
                      boxShadow: theme.shadows.lg,
                      color: theme.colors.light.text.primary,
                    }}
                    labelStyle={{ color: theme.colors.light.text.primary }}
                  />
                  <Bar dataKey="value" fill={theme.colors.primary[500]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardBody>
        </ChartCard>
      </ChartsGrid>
    </div>
  );
};

export default GuardsAnalytics;

